import dayjs from 'dayjs';

import {
  CommunicationContextType,
  ComposeContextRowType,
  ComposeContextValueKind,
  IComposeContextSelection,
  IComposeContextSession,
  SportTermLookup,
  SportTerminology
} from '../../../lib/types';
import { numToTime } from '../../../lib/helpers/conversion.helpers';
import { pluralize } from '../../../lib/helpers/text.helpers';
import { CONTEXT_ROW_TYPES } from './context-selector.constants';
import { IContextOption } from './context-selector.types';

export const isValueKind = (type: ComposeContextRowType): type is ComposeContextValueKind =>
  type === ComposeContextValueKind.LEVEL || type === ComposeContextValueKind.TEAM;

// A picked option becomes the row's selection; value-kinds store `value`,
// id-kinds store `id`. Label persists so draft chips render after deletion.
export const buildRowFromOption = (
  type: ComposeContextRowType,
  option: IContextOption
): IComposeContextSelection =>
  isValueKind(type)
    ? { type, value: option.key, label: option.label }
    : { type, id: option.key, label: option.label };

// Picker copy for a session instance: "Tue Jan 14 · 6:00 PM · {location} {area} ({ageGroup})".
export const buildSessionOptionLabel = (session: IComposeContextSession): string => {
  const place = [session.location, session.area].filter(Boolean).join(' ');
  const base = [dayjs(session.date).format('ddd MMM D'), numToTime(session.start), place]
    .filter(Boolean)
    .join(' · ');
  return session.ageGroup ? `${base} (${session.ageGroup.name})` : base;
};

const summaryNoun = (type: ComposeContextRowType, termLookup: SportTermLookup): string => {
  switch (type) {
    case CommunicationContextType.AGE_GROUP:
      return 'age group';
    case CommunicationContextType.ASSESSMENT_SESSION:
      return termLookup[SportTerminology.SESSION].toLowerCase();
    case ComposeContextValueKind.LEVEL:
      return 'level';
    case ComposeContextValueKind.TEAM:
      return 'team';
    default:
      return termLookup[SportTerminology.ATHLETE].toLowerCase();
  }
};

// One-line audience summary parts in registry order, e.g. ["2 age groups",
// "1 player"] — sport nouns resolve per company (display copy only).
export const buildContextSummaryParts = (
  contexts: IComposeContextSelection[],
  termLookup: SportTermLookup
): string[] =>
  CONTEXT_ROW_TYPES.map((type) => {
    const count = contexts.filter((row) => row.type === type && (row.id || row.value)).length;
    return count ? `${count} ${pluralize(count, summaryNoun(type, termLookup))}` : '';
  }).filter(Boolean);
